import React, { useState, useEffect, Fragment } from "react";
import { useParams } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useDarkMode } from "../../contexts/DarkModeContext";

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  const { isDarkMode } = useDarkMode();

  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.9)",
          border: isDarkMode ? "1px solid rgba(71, 85, 105, 0.3)" : "1px solid rgba(255, 255, 255, 0.3)",
          borderRadius: "8px",
          padding: "12px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          color: isDarkMode ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.8)",
          fontSize: "13px",
          fontWeight: "500",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            fontSize: "14px",
            marginBottom: "4px",
            color: isDarkMode ? "rgba(255, 255, 255, 0.95)" : "rgba(0, 0, 0, 0.9)",
          }}
        >
          {new Date(label).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </div>
        {payload.map((entry, index) => {
          return (
            <div key={index} style={{ marginTop: "2px" }}>
              <span style={{ color: entry.color }}>
                {`Quantité : ${entry.value} kg/jour`}
              </span>
              {entry.payload.endDate && (
                <div style={{ marginTop: "2px" }}>
                  <span style={{ color: isDarkMode ? "#a78bfa" : "#7c3aed" }}>
                    {`Fin: ${new Date(entry.payload.endDate).toLocaleDateString("fr-FR", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}`}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

function MilkChart(props) {
  const [processedMilks, setProcessedMilks] = useState([]);
  const [yAxisDomain, setYAxisDomain] = useState([0, 5]);
  const [yAxisTicks, setYAxisTicks] = useState([0, 1, 2, 3, 4, 5]);
  const { isDarkMode } = useDarkMode();

  function processMilks(milks) {
    if (milks.length === 0) return [];

    // Sort milks by start date
    const sortedMilks = [...milks].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

    // Create step chart data - each period needs two points: start and end
    const processed = [];

    sortedMilks.forEach((milk, index) => {
      // Add start point of current period
      processed.push({
        x: milk.startDate,
        y: milk.quantity,
        endDate: milk.endDate,
        idmilk: milk.idmilk,
        isStart: true
      });

      // Add end point of current period (same quantity)
      processed.push({
        x: milk.endDate,
        y: milk.quantity,
        endDate: milk.endDate,
        idmilk: milk.idmilk,
        isEnd: true
      });
    });

    // Calculate Y-axis domain and ticks dynamically
    if (sortedMilks.length > 0) {
      const maxQuantity = Math.max(...sortedMilks.map(m => m.quantity));
      const minQuantity = Math.min(...sortedMilks.map(m => m.quantity));

      // Start at 0, end at next multiple of 1 above max quantity
      const maxDomain = Math.ceil(maxQuantity);
      const domain = [0, Math.max(maxDomain, 5)]; // Ensure minimum range of 0-5

      // Generate ticks from 0 to maxDomain in steps of 1
      const ticks = [];
      for (let i = 0; i <= domain[1]; i += 1) {
        ticks.push(i);
      }

      setYAxisDomain(domain);
      setYAxisTicks(ticks);
    }

    setProcessedMilks(processed);
  }

  let { id } = useParams();
  if (id === undefined) {
    id = "null";
  }

  useEffect(() => {
    processMilks(props.milks);
  }, [props]);

  return (
    <Fragment>
      <div className="mb-3 flex w-full flex-col rounded-xl border border-gray-200 bg-white p-3  dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-6">
          <span className="font-semibold dark:text-white">
            Historique des quantités de lait
          </span>
        </div>
        <div className="h-80 w-full  md:px-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorMilk" x1="0" y1="0" x2="0" y2="1">
                  {isDarkMode ? (
                    <>
                      <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#172554"
                        stopOpacity={0.0}
                      />
                    </>
                  ) : (
                    <>
                      <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#fef3c7"
                        stopOpacity={0.0}
                      />
                    </>
                  )}
                </linearGradient>
              </defs>

              <CartesianGrid vertical={false} opacity={isDarkMode ? 0.1 : 0.2} stroke={isDarkMode ? "#475569" : "#e2e8f0"} />
              <XAxis
                dataKey="x"
                tick={{ fill: isDarkMode ? "#94a3b8" : "#475569", fontSize: "12px" }}
                tickLine={false}
                axisLine={{ stroke: isDarkMode ? "#475569" : "#f1f5f9" }}
                tickFormatter={(value) => {
                  // Calculate weeks since first milk record
                  if (processedMilks.length > 0) {
                    const firstDate = new Date(processedMilks[0].x);
                    const currentDate = new Date(value);
                    const weeksDiff = Math.floor((currentDate - firstDate) / (1000 * 3600 * 24 * 7));
                    return `${weeksDiff} sem`;
                  }
                  return "";
                }}
              />
              <YAxis
                tick={{ fill: isDarkMode ? "#94a3b8" : "#475569", fontSize: "12px" }}
                axisLine={false}
                tickLine={false}
                domain={yAxisDomain}
                ticks={yAxisTicks}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: "transparent" }}
              />
              <Line
                data={processedMilks}
                dataKey="y"
                label="Quantité"
                type="stepAfter"
                dot={false}
                stroke="#f59e0b"
                fill="url(#colorMilk)"
                strokeWidth={2}
                radius={[7, 7, 0, 0]}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-end gap-2">

          <span className="rounded-md border border-stone-300 bg-stone-50 p-1 text-sm font-semibold text-stone-700 dark:border-slate-500 dark:bg-slate-900/30 dark:text-slate-300">
            Total bu : {props.totalQuantity} kg
          </span><span className="rounded-md border border-amber-600 bg-amber-50 p-1 text-sm font-semibold text-amber-700 dark:border-amber-500 dark:bg-amber-900/30 dark:text-amber-300">
            Kilos par jour
          </span>
        </div>
      </div>
    </Fragment>
  );
}

export default MilkChart;
